import i18next from 'i18next';
import { exhaustiveCheck } from '@famly/stat_ts-utils_exhaustive-check';
/**
 * Resolves the copy for a failed OpenID Connect authentication.
 *
 * Kept separate from `getOpenIdErrorReason` and called during render rather than when the failure
 * is first recorded: resolving it once at mount would pin whichever language i18next happened to
 * have loaded at that moment, and the OIDC callback is the first screen to mount after a redirect
 * back — squarely inside the window where the user's locale is still being fetched. The language is
 * an argument rather than left implicit so the copy re-resolves once that fetch lands, which a
 * re-render alone would not do — see `useI18nextLanguage`.
 *
 * @param reason The reason the authentication failed
 * @param language The language to resolve in, as reported by `useI18nextLanguage`
 * @returns The title and description to show on the failure screen
 */
export const getOpenIdErrorMessage = (reason, language) => {
    const options = { lng: language };
    switch (reason) {
        case 'accessDenied':
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.accessDeniedTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.accessDeniedDescription, options),
            };
        case 'signInRequired':
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.signInRequiredTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.signInRequiredDescription, options),
            };
        case 'providerUnavailable':
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.providerUnavailableTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.providerUnavailableDescription, options),
            };
        case 'configuration':
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.configurationTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.configurationDescription, options),
            };
        case 'unknown':
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.unknownTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.unknownDescription, options),
            };
        default:
            exhaustiveCheck(reason);
            return {
                title: i18next.t($ => $.react.login.openid.translations.json.loginError.unknownTitle, options),
                description: i18next.t($ => $.react.login.openid.translations.json.loginError.unknownDescription, options),
            };
    }
};
